/**
 * @module app.shared
 */

import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import M from 'materialize-css';
import { MaterializeComponentRefresher } from 'app/shared/lib/materialize/component-refresher';
import { fireEventOn } from 'app/shared/lib/fire-events';
import { getKey, Key } from '../lib/keys';
import { addDelegatedEventListener, removeDelegatedEventListener } from '../lib/listen-events';

const CONFIRM_KEYS = [
    Key.Space
]

function countChildren(element: Element) {
    return Array.from(element.children).filter((child) => child instanceof HTMLLIElement).length;
}

function calculateIndex(list: Element, item: Element): number {
    return Array.from(list.children).indexOf(item);
}

export interface ICollapseCounts {
    open: number;
    closed: number;
    total: number;
}

export interface ICollapse extends IAccordion {
    openAll(): void;
    closeAll(): void;
    getCounts(): ICollapseCounts;
}

export interface IAccordion {
    open(index: number): void;
    close(index: number): void;
}

export interface ICollapseEventDetails {
    index: number,
    focusActive?: boolean
};
export type CollapseEvent = CustomEvent<ICollapseEventDetails>;

@autoinject()
abstract class CollapseBase {

    inDuration: number = 300;
    outDuration: number = 300;

    protected accordion: boolean = false;
    protected count: number = 0;
    public defaultIndex: number = -1;
    protected currentIndex: number = 0;
    protected instance: M.Collapsible | null = null;
    protected mutationObserver: MutationObserver = new MutationObserver(this.handleMutation.bind(this));

    constructor(protected element: Element, protected refresher: MaterializeComponentRefresher<M.Collapsible>) {
        element.classList.add('collapsible');
        element.addEventListener('openend', (event) => this.toggleAriaExpanded(event as CollapseEvent, element, true));
        element.addEventListener('closeend', (event) => this.toggleAriaExpanded(event as CollapseEvent, element, false));
    }

    public open(index: number): void {
        if(this.instance) {
            this.instance.open(index);
        }
    }

    public close(index: number): void {
        if(this.instance) {
            this.instance.close(index);
        }
    }

    protected handleKeydown = (event: KeyboardEvent) => {
        // need to ensure keydown event is delegated to the collapsible-header
        const target = event.target as HTMLElement;
        if(!event.defaultPrevented && !!target?.matches('.collapsible-header') && CONFIRM_KEYS.includes(getKey(event) as Key)) {
            event.preventDefault();
            target?.click();
        }
    };

    public toggleAriaExpanded(event: CollapseEvent, element: Element, toggle: boolean): void {
        if(!element) return;
        const index = event.detail.index;
        const headerElement = element.children[index].querySelector('.collapsible-header');
        headerElement?.setAttribute('aria-expanded', toggle ? 'true' : 'false');
        if(!headerElement?.classList.contains('ess-disabled')) {
            headerElement?.setAttribute('tabindex', '0');
        }
    }

    public attachAttributesToElements() {
        const headers = this.element.querySelectorAll('.collapsible-header');
        const bodyContents = this.element.querySelectorAll('.collapsible-body');
        headers.forEach((element) => {
            element.setAttribute('role', 'button');
            if(element.closest('li')?.classList.contains('active')) {
                element.setAttribute('aria-expanded', 'true');
            } else {
                element.setAttribute('aria-expanded', 'false');
            }
            if(element.classList.contains('ess-disabled')) {
                element.removeAttribute('tabindex');
            }
        });
        bodyContents.forEach((element) => {
            element.setAttribute('role', 'region');
        });
    }

    protected attached() {
        const { element, inDuration, outDuration, accordion } = this;
        this.refresher.initialize(() => {
            this.instance = M.Collapsible.init(element, {
                accordion,
                inDuration,
                outDuration,
                onOpenStart: (el: Element) => {
                    this.currentIndex = calculateIndex(element, el);
                    const detail: ICollapseEventDetails = { index: this.currentIndex };
                    fireEventOn(el, 'openstart', detail);
                    fireEventOn(element, 'openstart', detail);
                },
                onOpenEnd: (el: Element) => {
                    const detail: ICollapseEventDetails = { index: calculateIndex(element, el) };
                    fireEventOn(el, 'openend', detail);
                    fireEventOn(element, 'openend', detail)
                },
                onCloseStart: (el: Element) => {
                    const detail: ICollapseEventDetails = { index: calculateIndex(element, el) };
                    fireEventOn(el, 'closestart', detail);
                    fireEventOn(element, 'closestart', detail)
                },
                onCloseEnd: (el: Element) => {
                    const detail: ICollapseEventDetails = { index: calculateIndex(element, el) };
                    fireEventOn(el, 'closeend', detail);
                    fireEventOn(element, 'closeend', detail)
                },
            });
            this.count = countChildren(this.element);
            if(this.defaultIndex >= 0) {
                this.instance.open(this.defaultIndex);
            }
            return this.instance;
        });

        // add click handler to collapse header for ADA compliance
        addDelegatedEventListener(element, 'keydown', this.handleKeydown);
        this.attachAttributesToElements();
        this.mutationObserver.observe(this.element, { childList: true });
    }

    protected detached() {
        this.refresher.destroy();
        this.instance = null;

        // remove click handler for collapse header
        removeDelegatedEventListener(this.element, 'keydown', this.handleKeydown);
    }

    protected handleMutation(mutations: MutationRecord[], observer: any) {
        if(mutations.some(mutation => mutation.addedNodes.length > 0)) {
            this.attachAttributesToElements();
        }
    }

}

@bindable({ name: 'inDuration', defaultBindingMode: bindingMode.oneTime })
@bindable({ name: 'outDuration', defaultBindingMode: bindingMode.oneTime })
@autoinject()
export class EssAccordionCustomAttribute extends CollapseBase implements IAccordion {

    accordion = true;

    constructor(protected element: Element, protected refresher: MaterializeComponentRefresher<M.Collapsible>) {
        super(element, refresher);
        element.classList.add('accordion');
    }

    public getCurrentIndex(): number {
        return this.currentIndex;
    }

    public calculateIndex(item: Element): number {
        return calculateIndex(this.element, item);
    }

}

@bindable({ name: 'inDuration', defaultBindingMode: bindingMode.oneTime })
@bindable({ name: 'outDuration', defaultBindingMode: bindingMode.oneTime })
@autoinject()
export class EssCollapseCustomAttribute extends CollapseBase implements ICollapse {

    constructor(protected element: Element, protected refresher: MaterializeComponentRefresher<M.Collapsible>) {
        super(element, refresher);
        element.classList.add('expandable');
    }

    public openAll(): void {
        for(let i = this.count - 1; i >= 0; i--) {
            this.open(i);
        }
    }

    public closeAll(): void {
        for(let i = this.count - 1; i >= 0; i--) {
            this.close(i);
        }
    }

    public getCounts(): ICollapseCounts {
        let open: number = 0;
        let closed: number = 0;
        const children = Array.from(this.element.children).filter((child) => child instanceof HTMLLIElement);
        for(const child of children) {
            if(child.classList.contains('active')) {
                open++;
            }
            else {
                closed++;
            }
        }
        return { open, closed, total: children.length };
    }

    protected attached(): void {
        super.attached();
        this.count = countChildren(this.element);
    }
}
